import type { WebsiteContent } from './website';

export type SeoCheckStatus = 'pass' | 'warn' | 'fail';

export interface SeoCheck {
  id: string;
  /** Plain-language name of what is being checked. */
  label: string;
  status: SeoCheckStatus;
  /** What the current state actually is. */
  finding: string;
  /** Why this matters, in everyday terms. */
  why: string;
  /** What to do about it when it is not passing. */
  fix: string;
  weight: number;
}

/** Scores the parts of search visibility the site itself controls. */
export function runSeoChecks(content: WebsiteContent): SeoCheck[] {
  const title = content.seoTitle.trim();
  const description = content.seoDescription.trim();
  const location = content.seoLocation.trim();
  const genre = content.seoGenre.trim();
  const siteUrl = content.siteUrl.trim();
  const shareImage = content.shareImageUrl.trim();
  const bandName = content.bandName.trim();
  const visibleShows = content.showsHidden ? [] : content.shows;
  const socials = [content.instagramUrl, content.tiktokUrl, content.spotifyUrl, content.youtubeUrl].filter((url) => url.trim());
  const galleryWithoutCaptions = content.galleryImages.filter((image) => !image.caption.trim()).length;
  const aboutWords = content.aboutBody.trim() ? content.aboutBody.trim().split(/\s+/).length : 0;
  const namedMembers = content.aboutMembers.filter((member) => member.name.trim()).length;
  const cityWord = location.split(',')[0].trim().toLowerCase();
  const mentionsCity = Boolean(cityWord) && content.aboutBody.toLowerCase().includes(cityWord);
  const credits =
    content.aboutVenues.filter((venue) => venue.venue.trim()).length +
    content.aboutAwards.filter((award) => award.title.trim()).length +
    content.aboutEndorsements.filter((entry) => entry.quote.trim()).length;

  return [
    {
      id: 'title',
      label: 'Page title',
      weight: 3,
      status: !title ? 'fail' : title.length > 60 ? 'warn' : bandName && !title.toLowerCase().includes(bandName.toLowerCase().slice(0, 6)) ? 'warn' : 'pass',
      finding: !title
        ? 'No page title set.'
        : `${title.length} characters${title.length > 60 ? ' — Google will cut this off' : ''}.`,
      why: 'This is the blue headline someone clicks in Google. It is the single biggest factor in whether they click you or the next result.',
      fix: 'Write 50–60 characters that include your band name, your genre, and your city. Example: "Cooper Ty Band · Texas Country from Fort Worth".',
    },
    {
      id: 'description',
      label: 'Search description',
      weight: 3,
      status: !description ? 'fail' : description.length < 70 || description.length > 160 ? 'warn' : 'pass',
      finding: !description
        ? 'No description set.'
        : `${description.length} characters${description.length > 160 ? ' — the end will be cut off' : description.length < 70 ? ' — room for more detail' : ''}.`,
      why: 'The grey text under your link in Google. It does not change your ranking, but it decides whether people click.',
      fix: 'Write 120–155 characters covering what you play, where you are from, and what people can do on the site: tour dates, tickets, music, merch.',
    },
    {
      id: 'site-url',
      label: 'Website address',
      weight: 2,
      status: !siteUrl ? 'fail' : !siteUrl.startsWith('https://') ? 'warn' : 'pass',
      finding: !siteUrl ? 'No address set.' : siteUrl,
      why: 'Tells Google which address is the real one, so your ranking is not split between different versions of the site.',
      fix: 'Enter your full address starting with https:// — for example https://coopertyband.com.',
    },
    {
      id: 'location',
      label: 'Home city and genre',
      weight: 2,
      status: location && genre ? 'pass' : location || genre ? 'warn' : 'fail',
      finding: [location || 'No city', genre || 'No genre'].join(' · '),
      why: 'Most searches that find working bands are local — "country band Fort Worth", "live music near me". Without a city you are invisible to those.',
      fix: 'Add your home city and state, plus the genre people would actually type.',
    },
    {
      id: 'shows',
      label: 'Tour dates published',
      weight: 3,
      status: visibleShows.length >= 2 ? 'pass' : visibleShows.length === 1 ? 'warn' : 'fail',
      finding: content.showsHidden
        ? 'Tour dates are hidden on the site.'
        : `${visibleShows.length} date${visibleShows.length === 1 ? '' : 's'} published.`,
      why: 'Your dates are published as event data, which is what lets Google show your shows directly in search and on Maps. It is the highest-value thing on a band site.',
      fix: 'Publish your upcoming dates in Website Studio, or sync them from Bandsintown, and make sure the section is visible.',
    },
    {
      id: 'ticket-links',
      label: 'Ticket links on every date',
      weight: 2,
      status: !visibleShows.length
        ? 'warn'
        : visibleShows.every((show) => show.ticketUrl.trim() && show.ticketUrl !== '#')
          ? 'pass'
          : 'warn',
      finding: !visibleShows.length
        ? 'No dates to check yet.'
        : `${visibleShows.filter((show) => show.ticketUrl.trim() && show.ticketUrl !== '#').length} of ${visibleShows.length} dates have a real ticket link.`,
      why: 'A date without a ticket link cannot show a "Buy tickets" action in search results, and fans who arrive ready to buy leave instead.',
      fix: 'Add the ticketing URL to each show in Website Studio.',
    },
    {
      id: 'share-image',
      label: 'Link preview image',
      weight: 2,
      status: shareImage ? 'pass' : 'fail',
      finding: shareImage ? 'Set.' : 'No preview image.',
      why: 'When someone texts your link or posts it, this is the photo that appears. Without it the link looks broken and gets far fewer clicks.',
      fix: 'Add a wide photo, around 1200 × 630 pixels. A live shot works better than a logo.',
    },
    {
      id: 'socials',
      label: 'Social profiles linked',
      weight: 2,
      status: socials.length >= 3 ? 'pass' : socials.length ? 'warn' : 'fail',
      finding: `${socials.length} of 4 linked.`,
      why: 'Linking your official profiles tells Google these all belong to the same band, which is how you get the info panel on the right side of results.',
      fix: 'Add your Instagram, TikTok, Spotify, and YouTube links in Website Studio.',
    },
    {
      id: 'music',
      label: 'Streaming links set',
      weight: 1,
      status: content.musicHidden
        ? 'warn'
        : content.musicPrimaryUrl.trim() || content.musicSecondaryUrl.trim()
          ? 'pass'
          : 'fail',
      finding: content.musicHidden
        ? 'Music section is hidden.'
        : [content.musicPrimaryUrl.trim() && 'Spotify', content.musicSecondaryUrl.trim() && 'Apple Music']
            .filter(Boolean)
            .join(' · ') || 'No streaming links set.',
      why: 'Linking out to the platforms that host your catalog tells Google your site is the official hub for your music.',
      fix: 'Add your Spotify and Apple Music links to the Music section in Website Studio.',
    },
    {
      id: 'about',
      label: 'About section written',
      weight: 3,
      status: content.aboutHidden
        ? 'fail'
        : aboutWords >= 60 && namedMembers > 0 && mentionsCity
          ? 'pass'
          : aboutWords >= 25
            ? 'warn'
            : 'fail',
      finding: content.aboutHidden
        ? 'About section is hidden.'
        : `${aboutWords} words · ${namedMembers} member${namedMembers === 1 ? '' : 's'} named · ${mentionsCity ? 'city mentioned' : 'no city mentioned'}.`,
      why: 'Google can only match searches to words that exist on the page. Without sentences naming your members, your hometown, and your genre, searches for a person’s name or "country band Fort Worth" cannot find you at all.',
      fix: 'Write at least 60 words in the About section naming every member, your home city and state, your genre, and venues you have played.',
    },
    {
      id: 'about-credibility',
      label: 'Venue, award, and artist credits',
      weight: 2,
      status: credits >= 5 ? 'pass' : credits > 0 ? 'warn' : 'fail',
      finding: `${content.aboutVenues.filter((v) => v.venue.trim()).length} venues · ${content.aboutAwards.filter((a) => a.title.trim()).length} awards · ${content.aboutEndorsements.filter((e) => e.quote.trim()).length} artist quotes.`,
      why: 'Named venues, awards, and other artists are the specific phrases people search, and they are what tells Google you are a working act rather than a hobby page.',
      fix: 'Add the venues you have played, any contest wins, and quotes from other artists on the About page.',
    },
    {
      id: 'about-metadata',
      label: 'About page title & description',
      weight: 1,
      status: content.aboutSeoTitle.trim() && content.aboutSeoDescription.trim() ? 'pass' : 'warn',
      finding: content.aboutSeoTitle.trim()
        ? `${content.aboutSeoTitle.length} character title · ${content.aboutSeoDescription.length} character description.`
        : 'Using the homepage title and description.',
      why: 'Each page needs its own title and description, or Google treats them as duplicates and picks one to ignore.',
      fix: 'Write a title and description specific to the About page in Website Studio.',
    },
    {
      id: 'photo-captions',
      label: 'Photo captions',
      weight: 1,
      status: !content.galleryImages.length ? 'warn' : galleryWithoutCaptions === 0 ? 'pass' : 'warn',
      finding: !content.galleryImages.length
        ? 'No photos yet.'
        : `${galleryWithoutCaptions} photo${galleryWithoutCaptions === 1 ? '' : 's'} without a caption.`,
      why: 'Search engines cannot see what is in a photo. Captions naming the venue and city add real local search terms to your page.',
      fix: 'Caption each photo with the venue and city, like "Gruene Hall · New Braunfels, TX".',
    },
    {
      id: 'booking-email',
      label: 'Booking contact',
      weight: 1,
      status: content.bookingEmail.trim() ? 'pass' : 'warn',
      finding: content.bookingEmail.trim() || 'No booking email.',
      why: 'Promoters searching for you need a way to make contact within seconds of landing on the page.',
      fix: 'Add a booking email in Website Studio.',
    },
  ];
}

export function getSeoScore(checks: SeoCheck[]): number {
  const total = checks.reduce((sum, check) => sum + check.weight, 0);
  const earned = checks.reduce(
    (sum, check) => sum + check.weight * (check.status === 'pass' ? 1 : check.status === 'warn' ? 0.5 : 0),
    0,
  );
  return total ? Math.round((earned / total) * 100) : 0;
}

export interface SeoLaunchStep {
  id: string;
  title: string;
  minutes: string;
  summary: string;
  steps: string[];
  linkLabel: string;
  linkUrl: string;
}

/** Work that happens outside the site — the part most artists never do. */
export const seoLaunchSteps: SeoLaunchStep[] = [
  {
    id: 'search-console',
    title: 'Tell Google your site exists',
    minutes: '10 min',
    summary:
      'Google finds most sites eventually, but Search Console tells it today — and then shows you exactly which searches bring people to you.',
    steps: [
      'Go to Google Search Console and sign in with a Google account.',
      'Choose "URL prefix" and enter your full site address.',
      'Pick the DNS verification method and copy the record it gives you.',
      'Paste that record where you bought your domain, then click Verify.',
      'Once verified, paste your homepage into the search bar at the top and choose "Request indexing".',
    ],
    linkLabel: 'Open Search Console',
    linkUrl: 'https://search.google.com/search-console',
  },
  {
    id: 'business-profile',
    title: 'Claim your Google profile',
    minutes: '15 min',
    summary:
      'This is the panel that appears on the right side of Google with your photo, links, and upcoming shows. It is free and most bands skip it.',
    steps: [
      'Search your band name on Google and look for "Claim this knowledge panel".',
      'If nothing appears, create a Google Business Profile as a performing artist.',
      'Verify you represent the band using your official email or social account.',
      'Add your photo, genre, home city, website, and social links.',
    ],
    linkLabel: 'Open Google Business',
    linkUrl: 'https://www.google.com/business/',
  },
  {
    id: 'platform-links',
    title: 'Point every platform at your site',
    minutes: '20 min',
    summary:
      'Google trusts a site more when established platforms link to it. Your own profiles are the easiest links you will ever get.',
    steps: [
      'Add your website to your Spotify for Artists profile.',
      'Add it to Apple Music for Artists, Bandsintown, and YouTube.',
      'Put the link in your Instagram, TikTok, and Facebook bios.',
      'Use the same address everywhere — always the https:// version.',
    ],
    linkLabel: 'Open Spotify for Artists',
    linkUrl: 'https://artists.spotify.com',
  },
  {
    id: 'local-listings',
    title: 'Get listed where you play',
    minutes: 'Ongoing',
    summary:
      'Venue pages, local press, and event calendars are the strongest links a working band can earn, and they come from shows you are already playing.',
    steps: [
      'Ask each venue to link your website on their event page.',
      'Submit shows to local event calendars and alt-weekly listings.',
      'Send your site link with every press or radio mention.',
      'Keep your Bandsintown dates current so aggregators pick them up.',
    ],
    linkLabel: 'Open Bandsintown for Artists',
    linkUrl: 'https://artists.bandsintown.com',
  },
];
